import { Clipboard } from '@angular/cdk/clipboard';
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Params, RouterModule } from '@angular/router';

import { PnlComponent } from '../pnl/pnl.component';
import { RunHistory } from '../runs/run-history.model';
import { RunStatus } from '../runs/run-status.enum';
import { Run } from '../runs/run.model';
import { StatusBadgeComponent } from '../status-badge/status-badge.component';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { RunSourceBadgeComponent } from '../run-source-badge/run-source-badge.component';
import { RunSource } from '../shared/enums/run-source.enum';
import { ParsedRunInfo, parseRunName } from '../shared/utils/run-parser.util';
import { PnLDeltaInfo } from '../shared/services/pnl-delta.service';
import { OptimisationStatus } from '../shared/enums/optimisation-status.enum';
import { StatusPipe } from '../status-badge/status.pipe';

@Component({
  selector: 'run-card',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    RouterModule,
    NgbTooltipModule,
    StatusBadgeComponent,
    PnlComponent,
    RunSourceBadgeComponent,
    StatusPipe
  ],
  templateUrl: './run-card.component.html',
  styleUrls: ['./run-card.component.scss']
})
export class RunCardComponent implements OnInit {
  @Input({ required: true }) run: Run = new Run();
  @Input() expanded: boolean = false;
  @Input() pnlDeltaInfo?: PnLDeltaInfo; // Pre-computed from parent
  /** Query for the MTI Comparison link: the list sends the run it should be compared with. */
  @Input() mtiQuery?: Params;
  @Output() toggle = new EventEmitter<string>();

  runStatus = RunStatus;
  runInfo: ParsedRunInfo = { source: RunSource.Cloud, baseConfiguration: null, displayName: '', tcDays: null };

  latestRunHistory: RunHistory | undefined;
  runHistoriesAsc: RunHistory[] | undefined;

  elapsedTime: string = "";

  get deltaTooltip(): string {
    if (!this.pnlDeltaInfo) return '';
    const base = `vs ${this.pnlDeltaInfo.matchedBaseRunName}`;
    if (this.pnlDeltaInfo.tcDays !== null) {
      return `${base} (${this.pnlDeltaInfo.tcDays} TC days)`;
    }
    return base;
  }

  constructor(private clipBoard: Clipboard) { }

  ngOnInit() {
    this.runInfo = parseRunName(this.run.name);
    this.loadLatestRunHistory(this.run.runHistories);
    this.loadRunHistoriesByDateTimeAsc(this.run.runHistories);
    this.calculateTimeElapsed();
  }

  onHeaderClick(event: Event) {
    event.preventDefault();
    this.toggle.emit(this.run.runId);
  }

  copyRunId() {
    this.clipBoard.copy(this.run.runId);
  }

  copyOutputFilePath() {
    if (this.run.optimisationResult?.outputFilePath) {
      this.clipBoard.copy(this.run.optimisationResult.outputFilePath);
    }
  }

  private loadLatestRunHistory(runHistories: RunHistory[]): void {
    if (runHistories.length > 0) {
      this.latestRunHistory = runHistories
        .reduce((a, b) => a.eventTime > b.eventTime ? a : b);
    }
  }

  private loadRunHistoriesByDateTimeAsc(runHistories: RunHistory[]): void {
    this.runHistoriesAsc = [...runHistories].sort((a, b) => new Date(a.eventTime).getTime() - new Date(b.eventTime).getTime());
  }

  /** Whether the run can be opened in MTI Comparison and P&L Explain: it finished, Optimal. */
  get canCompare(): boolean {
    return this.run.optimisationResult?.status === OptimisationStatus.Optimal && this.latestRunHistory?.status === RunStatus.Success;
  }

  /** Why a run can't be compared, in a line. The runs record no reason of their own. */
  get notComparableReason(): string {
    const last = this.latestRunHistory?.status;
    if (last === RunStatus.Started || last === RunStatus.Running) return 'Still running. MTI Compare and P&L Explain open once it finishes Optimal.';
    if (last === RunStatus.Failure) return 'The run failed, so there is no result to compare. TBTU recorded no reason.';
    if (this.run.optimisationResult?.status === OptimisationStatus.Infeasible) return 'TBTU found no feasible plan for this run.';
    return 'There is no result to compare yet.';
  }

  /** Start to finish (or to failure), as "1h 02m 05s". */
  private calculateTimeElapsed() {
    const end = this.latestRunHistory;
    const started = this.runHistoriesAsc?.find((h) => h.status == RunStatus.Started);
    if (!end || !started || (end.status != RunStatus.Success && end.status != RunStatus.Failure)) return;

    const totalSeconds = Math.max(0, Math.round((new Date(end.eventTime).getTime() - new Date(started.eventTime).getTime()) / 1000));
    const h = Math.floor(totalSeconds / 3600), m = Math.floor(totalSeconds / 60) % 60, sec = totalSeconds % 60;
    const pad = (n: number) => String(n).padStart(2, '0');
    this.elapsedTime = h > 0 ? `${h}h ${pad(m)}m ${pad(sec)}s` : `${m}m ${pad(sec)}s`;
  }
}
